import { useEffect, useMemo } from 'react';

import { usePathname } from 'next/navigation';

import { ToggleGroup as TG } from 'radix-ui';
import { Controller, useFormContext } from 'react-hook-form';

import { LineButton } from '@quick/components/LineButton';

import { useAppTranslation } from '@/app/i18n/client';
import Working from '@/assets/working.svg';
import { useCountry } from '@/layers/core/deal/services/CountryContext';

import { useFormDataContext } from './FormDataContext';
import type { IForm } from './types';

export const Lines = () => {
  const { setValue, control } = useFormContext<IForm>();
  const { support, clean } = useCountry();

  const { line, data, isLoading } = useFormDataContext();

  const { t } = useAppTranslation(['common', 'demand']);

  // Only show external for clients
  const pathname = usePathname();
  const operations = pathname?.includes('operations');
  const lines = useMemo(() => {
    const availables = data?.lines?.filter(
      ({ service_types, external_url, for_offer }) =>
        // Should not be listed if there are no service types
        // And the service is external
        // And the service is for offert
        (service_types.length > 0 || (external_url && !operations)) && for_offer
    );
    return availables
      ?.filter(({ line_filter }) =>
        operations
          ? true // Show all if operations
          : clean
            ? line_filter === 'clean'
            : line_filter !== 'clean'
      )
      ?.map(({ id, name, external_url, image }) => {
        return { id, name, external_url, image };
      });
  }, [clean, data?.lines, operations]);

  useEffect(() => {
    const offerables = lines?.filter(({ external_url }) => !external_url);
    if (offerables?.length === 1) setValue('line_id', offerables.at(0)?.id);
  }, [lines, setValue]);

  if (isLoading && !data)
    return (
      <>
        <div className="z-10 flex flex-wrap justify-evenly gap-7 sm:col-span-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-20 w-60 animate-pulse rounded bg-gray-100 dark:bg-gray-500"
            />
          ))}
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            className="z-10 h-32 animate-pulse rounded bg-gray-100 dark:bg-gray-500"
            key={i}
          />
        ))}
      </>
    );
  return (
    <Controller
      control={control}
      name="line_id"
      rules={{
        required: t('common:msg.required_field'),
      }}
      render={({ field: { value, ...field } }) =>
        lines?.length ? (
          <>
            <TG.Root
              type="single"
              aria-label="Line"
              className="animate-in fade-in-0 motion-safe:slide-in-from-bottom-4 z-10 grid
                grid-cols-[repeat(auto-fit,minmax(14rem,20rem))] justify-center gap-4
                duration-300 sm:col-span-2"
              {...field}
              value={value?.toString() ?? ''}
              onValueChange={(value) => {
                const line_id = value;
                const serviceTypes = data?.lines
                  .find(({ id }) => id.toString() === line_id.toString())
                  ?.service_types?.filter(({ for_offer }) => for_offer);
                if (serviceTypes?.length === 1) {
                  setValue('service_type_id', line?.service_types.at(0)?.id);
                }
                if (!line_id) setValue('service_type_id', 0);
                setValue('modality_id', 0);
                setValue('cargo_type_id', 0);
                field.onChange(line_id);
              }}
            >
              {lines?.map(({ id, name, image, external_url }) => {
                const props = external_url
                  ? { element: 'a', href: external_url }
                  : { 'aria-label': name };
                return (
                  <TG.Item value={id.toString()} asChild key={id} {...props}>
                    <LineButton
                      icon={image ?? 'cargo'}
                      title={t(
                        `demand:homeServices.titles.${
                          name.toLowerCase() as 'local inland freight'
                        }`,
                        { defaultValue: name }
                      )}
                      type="button"
                      className="bg-background-group dark:bg-background-group-dark"
                    />
                  </TG.Item>
                );
              })}
            </TG.Root>
            {value &&
              !lines.find(({ id }) => id.toString() === value.toString()) && (
                <p className="text-center font-semibold text-red-600 underline sm:col-span-2 dark:text-red-400">
                  {t('demand:demandForm.noLine')}
                </p>
              )}
          </>
        ) : (
          <div
            className="relative z-10 flex max-w-[calc(100vw-4rem)] items-center justify-center
              sm:col-span-2"
          >
            {value &&
            !lines?.find(({ id }) => id.toString() === value.toString()) ? (
              <p
                className="absolute z-10 w-5/6 max-w-lg rounded p-2 text-center font-semibold text-white
                  drop-shadow-lg backdrop-contrast-50"
              >
                {t('demand:demandForm.noLine')}
              </p>
            ) : (
              <>
                {/* oxlint-disable-next-line no-html-link-for-pages */}
                <a
                  href={support}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute z-10 w-5/6 max-w-lg rounded p-2 text-center font-semibold text-white
                    drop-shadow-lg backdrop-contrast-50"
                >
                  {t('demand:working')}
                </a>
              </>
            )}
            <Working className="max-h-[calc(100vh-20rem)] drop-shadow" />
          </div>
        )
      }
    />
  );
};
